<template>
  <div class="card">
    <DataTable :value="products" dataKey="product_id">
      <Column
          v-for="col in columns"
          :key="col.field"
          :field="col.field"
          :header="col.header"
          :headerStyle="getHeaderStyle(col)"
          :headerClass="col.headerClass"
          :styles="{ 'text-align': col.align }"
      >
        <!-- Editable number -->
        <template v-if="col.type === 'editable'" #body="slotProps">
          <InputText
              v-if="editableRowId === slotProps.data.product_id"
              v-model.number="slotProps.data[col.field]"
              @input="updateTotal(slotProps.data)"
          />
          <span v-else>
            {{ col.field === 'quantity' ? slotProps.data[col.field] : slotProps.data[col.field].toFixed(2) }}
          </span>
        </template>

        <!-- Editable text -->
        <template v-else-if="col.type === 'editable_text'" #body="slotProps">
          <InputText
              v-if="editableRowId === slotProps.data.product_id && special_product_codes.includes(slotProps.data.product_code)"
              v-model="slotProps.data[col.field]"
          />
          <span v-else>
            {{ slotProps.data[col.field] }}
          </span>
        </template>

        <!-- Editable price -->
        <template v-else-if="col.type === 'editable_price'" #body="slotProps">
          <InputNumber
              v-if="editableRowId === slotProps.data.product_id"
              inputClass="text-right"
              v-model="slotProps.data[col.field]"
              mode="currency"
              currency="GBP"
              locale="en-GB"
              @input="updateTotal(slotProps.data)"
              @update:modelValue="updateTotal(slotProps.data)"
              @value-change="updateTotal(slotProps.data)"
          />
          <span v-else>
            {{ formatCurrency(slotProps.data[col.field]) }}
          </span>
        </template>

        <!-- Tax -->
        <template v-else-if="col.type === 'tax_info'" #body="slotProps">
          <div v-if="editableRowId === slotProps.data.product_id">
            <select
                class="w-full bge-input bge-select rounded"
                v-model="slotProps.data.tax_code"
                @change="updateTaxRate(slotProps.data)"
            >
              <option
                  v-for="tax in slotProps.data.tax_codes"
                  :key="tax.data_master_id"
                  :value="tax.name"
              >
                {{ tax.name }} ({{ tax.value }})
              </option>
            </select>
          </div>
          <div v-else>
            {{ slotProps.data.tax_code }} ({{ slotProps.data.tax_rate }})
          </div>
        </template>

        <!-- Total -->
        <template v-else-if="col.type === 'amount'" #body="slotProps">
          <span class="text-right">
            £{{ slotProps.data.total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') }}
          </span>
        </template>

        <!-- Total With Tax -->
        <template v-else-if="col.type === 'total_with_tax'" #body="slotProps">
          <span class="text-right">
            £{{ slotProps.data.total_with_tax.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') }}
          </span>
        </template>

        <!-- Actions -->
        <template v-else-if="col.type === 'actions'" #body="slotProps">
          <div class="flex justify-end items-center">
            <AppButton
                v-if="editableRowId === slotProps.data.product_id"
                color="blue"
                label=""
                :icon="true"
                class="mr-1"
                @click="saveRow(slotProps.data)"
            >
              <template v-slot:icon>
                <Icon iconType="save"/>
              </template>
            </AppButton>

            <AppButton
                v-if="editableRowId === slotProps.data.product_id && products.length !== 0"
                color="gray"
                label=""
                :icon="true"
                class="mr-1"
                @click="cancelEdit(slotProps.data)"
            >
              <template v-slot:icon>
                <Icon iconType="close"/>
              </template>
            </AppButton>

            <AppButton
                v-else
                color="blue"
                label=""
                :icon="true"
                class="mr-1"
                @click="editRow(slotProps.data.product_id)"
            >
              <template v-slot:icon>
                <Icon iconType="edit"/>
              </template>
            </AppButton>

            <AppButton
                color="red"
                label=""
                :icon="true"
                class="mr-1"
                @click="deleteRow(slotProps.data.product_id)"
            >
              <template v-slot:icon>
                <Icon iconType="delete"/>
              </template>
            </AppButton>
          </div>
        </template>
      </Column>

      <template #empty>
        No Items.
      </template>
    </DataTable>

    <portal to="overlay-outlet">
      <panel
          :showing="addReceivedItemPanelOpen"
          @close="addReceivedItemPanelOpen = false"
          title="Set Part Delivery"
      >
        <AddReceivedItem
            :orderItem="selectedOrderItem"
            @update-received-items="updateReceivedItems"
            :sales_order_id="sales_order_id"
        />
      </panel>
    </portal>
  </div>
</template>

<script>
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";
import Panel from "@/components/Panel.vue";
import AddReceivedItem from "@/views/HR/Purchases/Components/AddReceivedItem.vue";

export default {
  mixins: [notificationMixin],
  components: {
    AddReceivedItem,
    Panel,
    AppButton,
    Icon
  },
  props: {
    initialProducts: {
      type: Array,
      required: true,
    },
    sales_order_id: {
      type: String,
    }
  },
  data() {
    return {
      selectedOrderItem: null,
      selectedOrderItemIndex: -1,
      addReceivedItemPanelOpen: false,
      products: [],
      editableRowId: null,
      originalProductData: {},
      columns: [
        { field: 'quantity', header: 'Quantity', type: 'editable', width: '10%' },
        { field: 'product_code', header: 'Boston Code', width: '10%' },
        { field: 'description', header: 'Description', type: 'editable_text', width: '28%' },
        {
          field: 'price',
          header: 'Unit Cost',
          type: 'editable_price',
          width: '10%',
          align: 'right',
          headerClass: 'column-h-text-right'
        },
        {
          field: '',
          header: '',
          type: '',
          width: '2%',
        },
        {
          field: 'tax_info',
          header: 'Tax Rate',
          type: 'tax_info',
          width: '10%',
        },
        {
          field: 'total',
          header: 'Total',
          type: 'amount',
          width: '10%',
          align: 'right',
          headerClass: 'column-h-text-right'
        },
        {
          field: 'total_with_tax',
          header: 'Total Value',
          type: 'total_with_tax',
          width: '10%',
          align: 'right',
          headerClass: 'column-h-text-right'
        },
        { header: '', type: 'actions', width: '10%' }
      ],
      special_product_codes: ["SPC1", "SPC2", "SPC3", "SPC4"],
    };
  },
  watch: {
    initialProducts(newProducts) {
      this.products = newProducts.slice();
    },
  },
  async mounted() {
    this.products = this.initialProducts.slice();
  },
  computed: {
    totalAmount() {
      return this.products.reduce(function (sum, product) {
        return sum + product.total;
      }, 0);
    }
  },
  methods: {
    async saveOrderItem(product) {
      const dbProduct = {
        product_id: product.product_id,
        product_code: product.product_code,
        supplier_product_code: product.supplier_product_code,
        quantity: Number(product.quantity),
        unit_price: Number(product.price),
        // Only a deliberate manual edit sets this; without it the server
        // reprices the line from the customer's band.
        unit_price_overridden: product.unit_price_overridden === true,
        tax_code: product.tax_code,
        tax_rate: product.tax_rate,
        total: Number(product.total),
        total_with_tax: Number(product.total_with_tax),
        description: product.description,
        received_items: product.received_items,
      };

      try {
        if (product.isNew) {
          const createdProduct = await this.SalesOrderService.createSalesItem(this.sales_order_id, dbProduct);
          Object.assign(product, createdProduct);
        } else {
          if (product.sales_item_id) {
            await this.SalesOrderService.updateSalesItem(this.sales_order_id, product.sales_item_id, dbProduct);
          }
        }

        this.notifySuccess("Product " + (product.isNew ? "created" : "updated") + " successfully.");
        product.isNew = false;
        this.$emit("changed");
      } catch (error) {
        this.notifyError("Error " + (product.isNew ? "creating" : "updating") + " product.");
        console.error(error);
        throw error;
      }
    },

    async deleteOrderItem(salesItemId) {
      try {
        await this.SalesOrderService.deleteSalesItem(this.sales_order_id, salesItemId);

        this.products = this.products.filter(function (p) {
          return p.sales_item_id !== salesItemId;
        });

        this.notifySuccess("Product deleted successfully.");
        this.$emit("changed");
      } catch (error) {
        this.notifyError("Error deleting product.");
        console.error(error);
      }
    },

    updateReceivedItems(receivedItems) {
      this.$emit("changed");
    },

    addPartDelivery(orderItem, index) {
      this.selectedOrderItemIndex = index;
      this.selectedOrderItem = orderItem;
      this.addReceivedItemPanelOpen = true;
    },

    getCurrentProducts() {
      return this.products;
    },

    async addProducts(productsArray) {
      let added = 0;
      for (const product of productsArray) {
        if (this.isDuplicate(product)) {
          this.notifyWarning("Product with ID " + product.product_id + " is already added.");
          continue;
        }

        const rawPrice = product.cost_price != null ? parseFloat(product.cost_price) : NaN;
        if (isNaN(rawPrice)) {
          const label = product.product_code || product.product_id;
          this.notifyError(`No price available for ${label}. Set a price band on the product before adding it to a sales order.`);
          continue;
        }
        const price = rawPrice;

        const defaultQuantity = 1;
        const quantity = (product.quantity !== undefined && product.quantity !== 0) ? product.quantity : defaultQuantity;

        const defaultTaxRate = 0.2;
        const defaultTaxCode = 'T1';

        const taxRate =
            product.tax_rate !== undefined && product.tax_rate !== null && product.tax_rate !== ''
                ? parseFloat(product.tax_rate)
                : defaultTaxRate;

        const taxCode =
            product.tax_code !== undefined && product.tax_code !== null && product.tax_code !== ''
                ? product.tax_code
                : defaultTaxCode;

        const total = price * quantity;
        const total_with_tax = total + (total * taxRate);

        const newProduct = {
          ...product,
          price: price,
          quantity: quantity,
          unit_price_overridden: false,
          tax_code: taxCode,
          tax_rate: taxRate,
          total: total,
          total_with_tax: total_with_tax,
          isNew: true
        };

        try {
          await this.saveOrderItem(newProduct);
          this.products.push(newProduct);
          added += 1;
        } catch (error) {
          console.error(error);
        }
      }
      return added;
    },

    isDuplicate(newProduct) {
      return this.products.some(function (existingProduct) {
        return existingProduct.product_id === newProduct.product_id;
      });
    },

    editRow(rowId) {
      const product = this.products.find(function (p) {
        return p.product_id === rowId;
      });

      if (product) {
        this.originalProductData[rowId] = JSON.parse(JSON.stringify(product));
      }

      this.editableRowId = rowId;
    },

    async saveRow(product) {
      if (!product.description || product.quantity === null || product.price === null) {
        this.notifyError("Fields cannot be empty.");
        return;
      }

      if (isNaN(product.quantity) || isNaN(product.price)) {
        this.notifyError("Quantity and Price must be numbers.");
        return;
      }

      product.total = product.quantity * product.price;
      product.total_with_tax = product.total + (product.total * product.tax_rate);

      // A changed price on a manual row edit is a deliberate override; a
      // quantity-only edit keeps the line on automatic band pricing.
      const original = this.originalProductData[product.product_id];
      if (!original || Number(original.price) !== Number(product.price)) {
        product.unit_price_overridden = true;
      }

      await this.saveOrderItem(product);

      this.editableRowId = null;
      delete this.originalProductData[product.product_id];
    },

    cancelEdit(product) {
      if (product.isNew) {
        this.products = this.products.filter(function (p) {
          return p.product_id !== product.product_id;
        });
      } else {
        const originalData = this.originalProductData[product.product_id];
        if (originalData) {
          const index = this.products.findIndex(function (p) {
            return p.product_id === product.product_id;
          });

          if (index !== -1) {
            this.products.splice(index, 1, originalData);
          }
        }
      }

      this.editableRowId = null;
      delete this.originalProductData[product.product_id];
    },

    deleteRow(rowId) {
      const product = this.products.find(function (p) {
        return p.product_id === rowId;
      });

      if (product && product.sales_item_id) {
        this.deleteOrderItem(product.sales_item_id);
      }
    },

    updateTotal(product) {
      product.total = product.quantity * product.price;
      product.total_with_tax = product.total + (product.total * product.tax_rate);
    },

    updateTaxRate(product) {
      const selectedTax = product.tax_codes.find(function (tax) {
        return tax.name === product.tax_code;
      });

      if (selectedTax) {
        product.tax_rate = parseFloat(selectedTax.value);
      }

      product.total_with_tax = product.total + (product.total * product.tax_rate);
    },

    getHeaderStyle(col) {
      return {
        width: col.width,
        textAlign: col['header-align'] || 'left',
      };
    },

    formatCurrency(value) {
      return '£' + parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
  }
};
</script>

<style scoped>
.total-amount {
  margin-top: 10px;
  font-weight: bold;
}

::v-deep .column-h-text-left .p-column-header-content {
  text-align: left;
  display: block !important;
}

::v-deep .column-h-text-center .p-column-header-content {
  text-align: center;
  display: block !important;
}

::v-deep .column-h-text-right .p-column-header-content {
  text-align: right;
  display: block !important;
}
</style>